import {ListItem} from "../../utils/radio-list/list-item.model";
import {ProductionDate} from "../manufacture/date";

export interface Color extends ListItem {
  readonly id: number,
}

export interface LensFilter {
  colors?: Color[],
  backSides?: Backside[],
  families?: Lens[],
  materials?: Material[]
}

export interface QueryFilter {
  colorQuery?: ColorQuery,
  backSideQuery?: BacksideQuery,
  familyQuery?: LensQuery,
  materialQuery?: MaterialQuery
}

export interface ColorQuery {
  lensId?: number,
  materialId?: number,
  lensType?: LensType,
  backSideId?: number
}

export interface Material extends ListItem {
  id: number,
  defaultColor?: Color,
  return?: boolean,
  backSideId?: number,
  colors?: number[]
}

export interface MaterialQuery {
  lensId?: number,
  colorId?: number,
  lensType?: LensType,
  backSideId?: number
}

export interface Backside extends ListItem {
  readonly id: number,
  hard?: boolean,
  soft?: boolean
}

export interface BacksideQuery {
  lensType?: LensType,
  materialId?: number,
  colorId?: number
}

export interface Lens extends ListItem {
  readonly id: number,
  backSide?: number,
  lensType?: string,
  materials?: number[]
}

export class LensFactory {
  static fromDetails(that: LensDetails): Lens {
    return {id: that.id, name: that.name};
  }
}

export type LensType = "HARD" | "SOFT"
export type BacksideName = "RT" | "BT" | "BTX" | "VPT" | "VPT-LENTI" | "SPH" | "MAC" | "BTC"

export interface LensDetails {
  readonly id,
  readonly name,
  readonly lensType: LensType,
  readonly backSideId: number,
  readonly features: { default: Array<LensFeature>, individual: Array<LensFeature> }
}

export interface LensQuery {
  type?: LensType,
  materialId?: number,
  colorId?: number,
  backSideId?: number,
  lensGroup?: string
}

export interface LensFeature {
  readonly id?: number,
  readonly type: "RANGE" | "ENUM" | "FIXED",
  readonly name: string,
  readonly required: boolean,
  readonly defaultValue?: number,
  readonly value?: number,
  readonly decimalFormat?: number
}

export class RangeFeature implements LensFeature {
  readonly type = "RANGE";

  constructor(
    readonly name: string,
    readonly required: boolean,
    readonly rangeMin: number,
    readonly rangeMax: number,
    readonly rangeStep: number,
    readonly defaultValue?: number,
    readonly decimalFormat?: number,
    readonly isDisable?: boolean,
  ) {
  }
}

export function isRangeFeature(feature: LensFeature): feature is RangeFeature {
  return feature.type === "RANGE";
}

export class EnumFeature implements LensFeature {
  readonly type = "ENUM";

  constructor(
    readonly name: string,
    readonly required: boolean,
    readonly values: Array<{ id: number, name: string }>,
    readonly defaultValue?: number
  ) {
  }
}

export class FixedFeature implements LensFeature {
  readonly type = "ENUM";

  constructor(
    readonly name: string,
    readonly required: boolean,
    readonly value: any
  ) {
  }
}

// todo make these subtypes of Article type
export class AccessoryItem implements ListItem {
  constructor(
    readonly id: number,
    readonly name: string,
    readonly description: string,
    readonly price: string,
    readonly priceInfo: string,
    readonly electronicOrdering: boolean,
    readonly imageUrl: string
  ) {
  }
}

export class CareItem implements ListItem {
  constructor(
    readonly id: number,
    readonly name: string,
    readonly description: string,
    readonly price: string,
    readonly priceInfo: string,
    readonly electronicOrdering: boolean,
    readonly imageUrl: string,
    readonly quantity: number,
    readonly careTypes: Array<CareItemType>
  ) {
  }
}

export enum CareItemType {
  HARD = "HARD",
  SOFT = "SOFT",
  EYE = "EYE",
  REWETTING = "REWETTING"
}

export class FeatureDecimalFormat {
  constructor(
    readonly featureName: string,
    readonly decimalFormat: number
  ) {
  }
}

export interface Preload {
  readonly backSides: Backside[],
  readonly families: Lens[],
  readonly materials: Material[],
  readonly colors: Color[],
  readonly details: LensDetails[],
  readonly offDays: ProductionDate[]
}
